import { AlertTriangle, Ban, RefreshCw } from 'lucide-react'
import { useState } from 'react'

import type { NotificationSummary, ProviderBalance } from '@/api/notifications-types'
import { useAbandonNotification, useRetryNotification } from '@/api/notifications-api'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTime } from '@/lib/datetime'

/**
 * A status that can be retried, and one that cannot.
 *
 * `Suppressed` is deliberately absent. It is not a failure — the institution decided not to send,
 * because the customer never consented to marketing or has opted out — and offering a retry would
 * invite somebody to send it anyway, which is the Act 843 breach the suppression exists to prevent.
 * `Abandoned` is absent for the opposite reason: somebody has already decided to stop.
 */
const RETRYABLE = new Set(['Failed', 'DeadLettered'])

export function ProviderBalancePanel({ balance }: { balance: ProviderBalance | undefined }) {
  if (!balance) {
    return null
  }

  // Unknown is not zero. A gateway that could not be asked must not be rendered as an empty
  // account, or the institution stops sending alerts it could perfectly well send.
  if (!balance.available) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Gateway credit</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-body">
          The gateway could not be asked how much credit is left. This is not the same as having
          none — messages are still being attempted.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gateway credit</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="numeric text-2xl font-semibold text-heading tabular-nums">
          {balance.balance.toLocaleString('en-GH')} {balance.unit}
        </p>
        {balance.isLow ? (
          <p className="mt-2 flex items-start gap-2 text-sm text-warning">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            Below the warning threshold of {balance.warningThreshold.toLocaleString('en-GH')}{' '}
            {balance.unit}. Top the account up before it stops sending.
          </p>
        ) : null}
        {balance.capturedAtUtc ? (
          <p className="mt-2 text-xs text-muted">as at {formatDateTime(balance.capturedAtUtc)}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function NotificationRow({ notification }: { notification: NotificationSummary }) {
  const retry = useRetryNotification()
  const abandon = useAbandonNotification()
  const [reason, setReason] = useState('')
  const [abandoning, setAbandoning] = useState(false)

  const retryable = RETRYABLE.has(notification.status)

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-heading">{notification.recipientName}</p>
            <p className="font-mono text-xs text-muted">
              {notification.destinationMasked} · {notification.channel} · {notification.recipientClass}
            </p>
          </div>
          <StatusBadge status={notification.status} />
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted">Template</dt>
            <dd className="text-body">
              {notification.templateKey ?? '—'}
              {notification.templateVersion !== null ? ` v${notification.templateVersion}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted">Attempts</dt>
            <dd className="text-body">{notification.attemptCount}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted">Queued</dt>
            <dd className="text-body">{formatDateTime(notification.queuedAtUtc)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-muted">Cost</dt>
            <dd>
              <MoneyDisplay
                value={notification.costMinor}
                currency={notification.currencyCode}
                className="text-left"
              />
            </dd>
          </div>
        </dl>

        {notification.suppressionReason ? (
          <p className="rounded-md border border-border bg-canvas p-3 text-sm text-body">
            {/* Not an error. The institution chose not to send this. */}
            Not sent on purpose: {notification.suppressionReason}
          </p>
        ) : null}

        {notification.lastError ? (
          <p className="rounded-md border border-danger/30 bg-danger-tint p-3 text-sm text-body">
            {notification.lastError}
          </p>
        ) : null}

        {notification.nextAttemptAtUtc ? (
          <p className="text-xs text-muted">
            next attempt {formatDateTime(notification.nextAttemptAtUtc)}
          </p>
        ) : null}

        {retryable ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={retry.isPending}
              onClick={() => retry.mutate(notification.id)}
            >
              <RefreshCw className="size-4" aria-hidden />
              Try again
            </Button>

            {abandoning ? (
              <div className="flex w-full flex-wrap items-center gap-2">
                <input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why are you giving up on this one?"
                  maxLength={500}
                  className="min-w-0 flex-1 rounded-md border border-border px-3 py-2 text-sm"
                />
                <Button
                  variant="danger"
                  size="sm"
                  disabled={reason.trim().length === 0 || abandon.isPending}
                  onClick={() =>
                    abandon.mutate(
                      { notificationId: notification.id, reason: reason.trim() },
                      { onSuccess: () => setAbandoning(false) },
                    )
                  }
                >
                  Abandon
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setAbandoning(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setAbandoning(true)}>
                <Ban className="size-4" aria-hidden />
                Give up on it
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
